import { useRouter } from "next/router";
import { MouseEvent, ReactNode } from "react";
import { useAppSelector } from "../../../../services/hooks/useReduxHooks";
import { useTranslationI18 } from "../../../../services/hooks/useTranslationI18";
import {
  selectIsWalletCustomer,
  selectWalletBalance,
  selectWalletCurrency,
} from "../../../../services/redux/selectors/walletSelectors";
import { PagesEnum } from "../../../../utils/enums";
import { isArabicLanguage } from "../../../../utils/help/localization/localization";
import { getPageLink } from "../../../../utils/help/navigation/navigation";
import { formatPointsAmount } from "../../../../utils/loyalty/payWithPoints";
import styles from "./WalletBalanceContent.module.scss";

/** What sits under the blur when signed out: a shape, never a real balance. */
const MASKED_BALANCE = 100;

/**
 * "Your wallet balance" and the amount, or the amount blurred behind a sign-in
 * link for a guest. Shared by the menu's Wallet tab and the Wallet page's
 * balance card; each surface brings its own `trailing` control, and says where
 * signing in returns to.
 */
export const WalletBalanceContent = ({
  trailing,
  signInReturnTo,
}: {
  trailing?: ReactNode;
  /**
   * The page sign-in comes back to (`?returnTo=`, getSignInReturnPage). The
   * Wallet page passes itself, as the story asks; without it, Home.
   */
  signInReturnTo?: PagesEnum;
}) => {
  const router = useRouter();
  const { t } = useTranslationI18();
  const isSignedIn = useAppSelector(selectIsWalletCustomer);
  const balance = useAppSelector(selectWalletBalance);
  const walletCurrency = useAppSelector(selectWalletCurrency);
  const restaurantCurrency = useAppSelector((state) => state.config.currency);
  const currency = walletCurrency ?? restaurantCurrency;
  const isArabic = isArabicLanguage();

  // Same entry to login as every other sign-in link. Its own action, even
  // inside a surface that is itself a link (the menu's Wallet tab), so the tap
  // does not also open the wallet.
  const goToLogin = (event: MouseEvent) => {
    event.stopPropagation();
    const { href, as } = getPageLink(router, PagesEnum.Login, {
      queryParams: signInReturnTo ? [`returnTo=${signInReturnTo}`] : [],
    });
    router.push(href, as);
  };

  return (
    <div className={styles.root} data-testid="wallet-balance-content">
      <div className={styles.balance_block}>
        <span className={styles.block_first}>
          <span className={styles.left}>
            <span className={styles.label}>
              {t("wallet:your-wallet-balance")}
            </span>
            {isSignedIn ? (
              <span
                className={styles.amount}
                data-testid="wallet-balance-amount"
              >
                {formatPointsAmount(balance, currency, isArabic)}
              </span>
            ) : (
              <span
                className={`${styles.amount} ${styles.masked}`}
                aria-hidden="true"
                data-testid="wallet-balance-masked"
              >
                {formatPointsAmount(MASKED_BALANCE, currency, isArabic)}
              </span>
            )}
          </span>
          {trailing && <span className={styles.right}>{trailing}</span>}
        </span>
        {!isSignedIn && (
          <button
            type="button"
            className={styles.sign_in_link}
            onClick={goToLogin}
            data-testid="wallet-sign-in-link"
          >
            {t("wallet:sign-in-to-see-balance")}
          </button>
        )}
      </div>
    </div>
  );
};
